import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileUploader,
  Input,
  Label,
  PriceEdit,
  RadioGroup,
  RadioGroupItem,
  SelectAuto,
} from '@hero/web-components';
import { FileTextIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import type { ApplyFormValues } from './types';
import type {
  FinancingNeedMock,
  FleetTierMock,
  RevenueTierMock,
} from '../../mocks';

const FLEET_TIER_KEYS: FleetTierMock[] = [
  'lt_10',
  '11_20',
  '21_50',
  '51_100',
  '101_200',
  '201_500',
  'gt_500',
];

const REVENUE_TIER_KEYS: RevenueTierMock[] = [
  'lt_1m',
  '1m_3m',
  '3m_6m',
  '6m_8m',
  '8m_10m',
  'gt_10m',
];

/**
 * Invoicing is implicit (the application is *for* an invoice) — we
 * surface only the carrier's OTHER, longer-horizon needs so the
 * underwriting packet understands what the relationship can grow
 * into.
 */
const OTHER_NEED_KEYS: FinancingNeedMock[] = [
  'trucks',
  'working_capital',
  'fuel',
  'other',
];

const FINANCIAL_STATEMENTS_ACCEPT: Record<string, string[]> = {
  'application/pdf': [],
};

export type ProfileFieldsetProps = {
  values: ApplyFormValues;
  onChange: <K extends keyof ApplyFormValues>(key: K, value: ApplyFormValues[K]) => void;
};

export function ProfileFieldset({ values, onChange }: ProfileFieldsetProps) {
  const { t } = useTranslation('m-financing');

  const toggleNeed = (need: FinancingNeedMock) => {
    const next = values.financingNeeds.includes(need)
      ? values.financingNeeds.filter(p => p !== need)
      : [...values.financingNeeds, need];
    onChange('financingNeeds', next);
  };

  return (
    <fieldset className="space-y-5">
      <legend className="mb-1">
        <h3 className="text-base font-semibold text-gray-900">
          {t('apply_form.profile.title')}
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          {t('apply_form.profile.description')}
        </p>
      </legend>

      <ProfileSection
        title={t('apply_form.profile.sections.fleet.title')}
        description={t('apply_form.profile.sections.fleet.description')}>
        <div className="space-y-5">
          <div className="max-w-md">
            <SelectAuto
              label={t('apply_form.profile.fields.fleet_tier.title')}
              placeholder={t('apply_form.profile.fields.fleet_tier.placeholder')}
              value={values.fleetTier}
              onChange={v => onChange('fleetTier', v as FleetTierMock)}
              options={FLEET_TIER_KEYS.map(key => ({
                value: key,
                label: t(`fleet_tier.${key}`),
              }))}
              required
            />
          </div>

          <YesNoField
            label={t('apply_form.profile.fields.owns_all_trucks.title')}
            hint={t('apply_form.profile.fields.owns_all_trucks.hint')}
            value={values.ownsAllTrucks}
            onChange={v => {
              onChange('ownsAllTrucks', v);
              if (v === 'yes') onChange('trucksOwnedCount', '');
            }}
            yesLabel={t('apply_form.profile.fields.owns_all_trucks.yes')}
            noLabel={t('apply_form.profile.fields.owns_all_trucks.no')}
            required
          />

          {values.ownsAllTrucks === 'no' && (
            <div className="max-w-xs space-y-1.5">
              <Label htmlFor="trucks-owned-count">
                {t('apply_form.profile.fields.trucks_owned_count.title')}
                <RequiredMark />
              </Label>
              <Input
                id="trucks-owned-count"
                type="number"
                min={1}
                required
                value={values.trucksOwnedCount}
                onChange={e => onChange('trucksOwnedCount', e.target.value)}
                placeholder={t('apply_form.profile.fields.trucks_owned_count.placeholder')}
              />
              <p className="text-xs text-gray-500">
                {t('apply_form.profile.fields.trucks_owned_count.hint')}
              </p>
            </div>
          )}
        </div>
      </ProfileSection>

      <ProfileSection
        title={t('apply_form.profile.sections.financials.title')}
        description={t('apply_form.profile.sections.financials.description')}>
        <div className="space-y-5">
          <div className="max-w-md">
            <SelectAuto
              label={t('apply_form.profile.fields.annual_revenue_tier.title')}
              placeholder={t('apply_form.profile.fields.annual_revenue_tier.placeholder')}
              value={values.annualRevenueTier}
              onChange={v => onChange('annualRevenueTier', v as RevenueTierMock)}
              options={REVENUE_TIER_KEYS.map(key => ({
                value: key,
                label: t(`revenue_tier.${key}`),
              }))}
              required
            />
          </div>

          <YesNoField
            label={t('apply_form.profile.financials.is_profitable.title')}
            value={values.isProfitable}
            onChange={v => onChange('isProfitable', v)}
            yesLabel={t('apply_form.profile.financials.is_profitable.yes')}
            noLabel={t('apply_form.profile.financials.is_profitable.no')}
          />

          <YesNoField
            label={t('apply_form.profile.fields.has_loans.title')}
            value={values.hasLoans}
            onChange={v => onChange('hasLoans', v)}
            yesLabel={t('apply_form.profile.fields.has_loans.yes')}
            noLabel={t('apply_form.profile.fields.has_loans.no')}
            required
          />

          {values.hasLoans === 'yes' && (
            <div className="max-w-sm space-y-1.5">
              <Label htmlFor="monthly-financing-payment">
                {t('apply_form.profile.fields.monthly_financing_payment.title')}
                <RequiredMark />
              </Label>
              <PriceEdit
                name="monthly-financing-payment"
                value={
                  values.monthlyFinancingPayment === ''
                    ? undefined
                    : Number(values.monthlyFinancingPayment)
                }
                onChange={v =>
                  onChange('monthlyFinancingPayment', v == null ? '' : String(v))
                }
                placeholder={t(
                  'apply_form.profile.fields.monthly_financing_payment.placeholder',
                )}
              />
              <p className="text-xs text-gray-500">
                {t('apply_form.profile.fields.monthly_financing_payment.hint')}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label>
              {t('apply_form.profile.financials.statements.title')}{' '}
              <span className="ms-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                {t('apply_form.profile.financials.statements.recommended_badge')}
              </span>
            </Label>
            <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-600">
              <p>{t('apply_form.profile.financials.statements.description')}</p>
              <ul className="mt-2 list-disc space-y-0.5 ps-4">
                <li>{t('apply_form.profile.financials.statements.documents.balance_sheet')}</li>
                <li>
                  {t('apply_form.profile.financials.statements.documents.income_statement')}
                </li>
                <li>{t('apply_form.profile.financials.statements.documents.cash_flow')}</li>
              </ul>
              <p className="mt-2 text-gray-500">
                {t('apply_form.profile.financials.statements.format_note')}
              </p>
            </div>
            <FileUploader
              files={values.financialStatements}
              onChange={files => onChange('financialStatements', files.slice(0, 1))}
              label={t('apply_form.profile.financials.statements.upload_label')}
              emptyIcon={<FileTextIcon className="size-5 text-gray-400" />}
              variant="compact"
              acceptedFileTypes={FINANCIAL_STATEMENTS_ACCEPT}
              hint={t('apply_form.profile.financials.statements.upload_hint')}
            />
            <p className="text-xs text-gray-500">
              {t('apply_form.profile.financials.statements.hint')}
            </p>
          </div>
        </div>
      </ProfileSection>

      <ProfileSection
        title={t('apply_form.profile.sections.future_needs.title')}
        description={t('apply_form.profile.sections.future_needs.description')}>
        <div className="flex flex-wrap gap-2">
          {OTHER_NEED_KEYS.map(need => {
            const isSelected = values.financingNeeds.includes(need);
            return (
              <button
                key={need}
                type="button"
                onClick={() => toggleNeed(need)}
                aria-pressed={isSelected}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  isSelected
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300',
                )}>
                {t(`financing_need.${need}`)}
              </button>
            );
          })}
        </div>
      </ProfileSection>
    </fieldset>
  );
}

function RequiredMark() {
  return <span className="text-destructive ms-1">*</span>;
}

/** Visual grouping — one topic per card so the wizard reads top-to-bottom. */
function ProfileSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-4 rounded-xl border border-gray-200 bg-gray-50/50 p-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {description ? (
          <p className="mt-0.5 text-xs text-gray-500">{description}</p>
        ) : null}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/** Consistent yes/no radio cards — stacked on mobile, side-by-side on sm+. */
function YesNoField({
  label,
  hint,
  value,
  onChange,
  yesLabel,
  noLabel,
  required,
}: {
  label: string;
  hint?: string;
  value: 'yes' | 'no' | '';
  onChange: (value: 'yes' | 'no') => void;
  yesLabel: string;
  noLabel: string;
  required?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required ? <RequiredMark /> : null}
      </Label>
      <RadioGroup
        value={value}
        onValueChange={v => onChange(v as 'yes' | 'no')}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Label
          className={cn(
            'flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm font-normal transition-colors hover:bg-white',
            value === 'yes'
              ? 'border-emerald-300 bg-emerald-50/80'
              : 'border-gray-200 bg-white',
          )}>
          <RadioGroupItem value="yes" />
          <span className="text-start leading-snug">{yesLabel}</span>
        </Label>
        <Label
          className={cn(
            'flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm font-normal transition-colors hover:bg-white',
            value === 'no'
              ? 'border-emerald-300 bg-emerald-50/80'
              : 'border-gray-200 bg-white',
          )}>
          <RadioGroupItem value="no" />
          <span className="text-start leading-snug">{noLabel}</span>
        </Label>
      </RadioGroup>
      {hint ? <p className="text-xs text-gray-500">{hint}</p> : null}
    </div>
  );
}
